import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useFilterValueCounts } from '../useFilterValueCounts'
import { MultiSelectCommandList } from './MultiSelectCommandList'
import type { ValuePickerProps } from './types'
import { useStatuses } from '@/routes/features/tasks/hooks/useWorkspaceStatuses'
import { StatusIcon } from '@/components/app/indicators/StatusIcon'
import { cn } from '@/lib/utils'

export function StatusFilterPicker({
  selectedValues,
  onToggleValue,
  onSetValues,
}: ValuePickerProps) {
  const { t } = useTranslation('tasks')
  const {
    all: { statuses },
    open: { statuses: openStatuses },
  } = useStatuses()
  const counts = useFilterValueCounts('status')

  const options = useMemo(
    () =>
      statuses.map((s) => ({
        value: s.id,
        label: s.name,
        icon: <StatusIcon progressType={s.progressType} statusId={s.id} size="sm" />,
      })),
    [statuses],
  )

  // Any open · Any closed (design 9b): pick every status on one side.
  const shortcuts = useMemo(() => {
    const openIds = new Set(openStatuses.map((s) => s.id))
    return [
      { key: 'anyOpen', ids: statuses.filter((s) => openIds.has(s.id)).map((s) => s.id) },
      { key: 'anyClosed', ids: statuses.filter((s) => !openIds.has(s.id)).map((s) => s.id) },
    ].filter((x) => x.ids.length > 0)
  }, [statuses, openStatuses])

  const isExactly = (ids: Array<string>) =>
    ids.length === selectedValues.length && ids.every((id) => selectedValues.includes(id))

  return (
    <MultiSelectCommandList
      options={options}
      selectedValues={selectedValues}
      onToggle={onToggleValue}
      searchPlaceholder={t('filters.search.statuses')}
      counts={counts}
      footer={
        onSetValues &&
        shortcuts.length > 0 && (
          <div className="flex items-center gap-1 border-t px-2 py-1.5 text-xs">
            {shortcuts.map((s, i) => (
              <span key={s.key} className="flex items-center gap-1">
                {i > 0 && <span className="text-muted-foreground">·</span>}
                <button
                  type="button"
                  className={cn(
                    'rounded px-1 py-0.5 hover:bg-accent',
                    isExactly(s.ids) ? 'font-medium text-foreground' : 'text-muted-foreground',
                  )}
                  onClick={() => onSetValues(s.ids)}
                >
                  {t(`filters.statusShortcuts.${s.key}`)}
                </button>
              </span>
            ))}
          </div>
        )
      }
    />
  )
}
